import { OpenPanel } from "@openpanel/web";
import User from "@models/user";
import { AnalyticsEventMap, AnalyticsEventName } from "./analytics.events";

interface FlowState {
  startedAt: number;
  data: Record<string, unknown>;
}

/**
 * OpenPanel wrapper for the store (WEB-4044). Replaces CountlyService +
 * mirrors the dashboard's AnalyticsService patterns:
 *
 * - Auto screen views (react-router drives the History API, which the SDK
 *   patches) and outgoing links; session replay with input masking.
 * - Same clientId as the dashboard/website → one journey per visitor across
 *   surfaces; `channel: "store"` separates them in dashboards.
 * - UTM last-touch (utm_*, sessionStorage) + first-touch (first_utm_*,
 *   localStorage) with the SAME storage keys as the other surfaces.
 * - Flow helpers give concluding events a duration_ms (checkout funnel).
 * - Dedicated revenue stream: pendingRevenue armed at payment_initiated,
 *   flushRevenue on confirmed success, clearRevenue on failure.
 * - NO group calls: the self-hosted server rejects group/assign_group
 *   payload types with 400 (see dashboard AnalyticsService for details).
 */
class AnalyticsService {
  private readonly op: OpenPanel | null = null;
  private readonly flowPrefixKey = "x_op_flow";
  private identifiedUserId: string | null = null;
  private readonly pendingConclusions = new Map<number, () => void>();

  constructor() {
    const apiUrl = process.env.REACT_APP_OPENPANEL_API_URL;
    const clientId = process.env.REACT_APP_OPENPANEL_CLIENT_ID;
    if (!apiUrl || !clientId) {
      // Missing env — analytics degrades silently, the store keeps working.
      return;
    }
    this.op = new OpenPanel({
      apiUrl,
      clientId,
      trackScreenViews: true,
      trackOutgoingLinks: true,
      trackAttributes: false,
      sessionReplay: {
        enabled: true,
        // Inputs stay masked (emails, phones, billing); page text is needed
        // to make replays useful for UX analysis.
        maskAllInputs: true,
        maskAllText: false,
        sampleRate: 1,
      },
    });

    this.op.setGlobalProperties({
      channel: "store",
      partner: "oneplay",
    });

    this.initUtmTracking();
    this.initErrorTracking();

    window.addEventListener("pagehide", () => this.flushPendingConclusions());
  }

  /**
   * Global client_error capture (window error + unhandledrejection),
   * throttled per message so a render loop can't flood the pipeline.
   */
  private readonly errorLastSent = new Map<string, number>();
  private initErrorTracking(): void {
    const THROTTLE_MS = 30_000;
    const report = (message: string, fatal: boolean) => {
      const key = message.slice(0, 200);
      const now = Date.now();
      if ((this.errorLastSent.get(key) ?? 0) + THROTTLE_MS > now) return;
      this.errorLastSent.set(key, now);
      this.track("client_error", { message: key, fatal });
    };
    window.addEventListener("error", (e) =>
      report(e.message || "unknown error", true)
    );
    window.addEventListener("unhandledrejection", (e) => {
      const reason = e.reason;
      report(
        (reason && (reason.message || String(reason))) || "unhandled rejection",
        false
      );
    });
  }

  /**
   * Attach api_error reporting to an axios client (one call per API class).
   * Failures still propagate — this only observes.
   */
  attachApiErrorTracking(
    client: {
      interceptors: {
        response: { use: (ok: any, err: (error: any) => any) => void };
      };
    },
    apiName: string
  ): void {
    client.interceptors.response.use(
      (response: unknown) => response,
      (error: any) => {
        const status: number | undefined = error?.response?.status;
        const isTimeout =
          error?.code === "ECONNABORTED" || error?.code === "ETIMEDOUT";
        if (isTimeout || (status !== undefined && status >= 500)) {
          const path: string = error?.config?.url ?? "";
          this.track("api_error", {
            endpoint: `${apiName}:${path}`.slice(0, 120),
            ...(status !== undefined ? { status } : {}),
            kind: isTimeout ? "timeout" : "server_error",
          });
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * UTM attribution: last-touch rides every event this session; first-touch
   * persists for lifetime attribution. Keys shared with the dashboard and
   * marketing site so attribution survives cross-surface handoffs.
   */
  private initUtmTracking(): void {
    if (!this.op) return;
    try {
      const keys = [
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "utm_term",
        "utm_content",
      ];
      const params = new URLSearchParams(window.location.search);
      const current: Record<string, string> = {};
      for (const key of keys) {
        const value = params.get(key);
        if (value) current[key] = value;
      }
      if (Object.keys(current).length > 0) {
        sessionStorage.setItem("op-utm-last", JSON.stringify(current));
        if (!localStorage.getItem("op-utm-first")) {
          localStorage.setItem("op-utm-first", JSON.stringify(current));
        }
      }
      const last = sessionStorage.getItem("op-utm-last");
      if (last) {
        this.op.setGlobalProperties(JSON.parse(last));
      }
      const first = localStorage.getItem("op-utm-first");
      if (first) {
        const firstProps: Record<string, string> = {};
        for (const [key, value] of Object.entries(
          JSON.parse(first) as Record<string, string>
        )) {
          firstProps[`first_${key}`] = value;
        }
        this.op.setGlobalProperties(firstProps);
      }
    } catch {
      // Storage unavailable (private mode) — attribution degrades silently.
    }
  }

  /** identify() on login; profile fields omitted when empty (API 400s on null). */
  identifyUser(user: User): void {
    if (!this.op || this.identifiedUserId === user.id) return;
    this.identifiedUserId = user.id;
    if (user.partnerId) {
      this.op.setGlobalProperties({
        partner_id: user.partnerId,
        partner_id_source: "user",
      });
    }
    this.op.identify({
      profileId: user.id,
      ...(user.firstName ? { firstName: user.firstName } : {}),
      ...(user.lastName ? { lastName: user.lastName } : {}),
      ...(user.email ? { email: user.email } : {}),
      ...(user.photo
        ? { avatar: new URL(user.photo, window.location.origin).href }
        : {}),
      properties: {
        ...(user.phone ? { phone: user.phone } : {}),
        ...(user.username ? { username: user.username } : {}),
        ...(user.type ? { user_type: user.type } : {}),
        ...(user.partnerId ? { partner_id: user.partnerId } : {}),
      },
    });
  }

  clearUser(): void {
    if (!this.op || !this.identifiedUserId) return;
    this.identifiedUserId = null;
    this.op.track("auth_logged_out");
    this.op.clear();
  }

  track<T extends AnalyticsEventName>(
    event: T,
    properties: AnalyticsEventMap[T]
  ): void {
    this.op?.track(event, properties as Record<string, unknown>);
  }

  // ── Revenue stream (MRR / attribution in OpenPanel) ────────────────────────
  revenue(amount: number, properties?: Record<string, unknown>): void {
    this.op?.revenue(amount, properties);
  }
  pendingRevenue(amount: number, properties?: Record<string, unknown>): void {
    this.op?.pendingRevenue(amount, properties);
  }
  flushRevenue(): void {
    this.op?.flushRevenue();
  }
  clearRevenue(): void {
    this.op?.clearRevenue();
  }

  // ── Flow helpers (concluding events carry duration_ms) ────────────────────
  startFlow<T extends AnalyticsEventName>(
    event: T,
    data: Partial<AnalyticsEventMap[T]> = {}
  ): void {
    this.setFlow(event, { startedAt: Date.now(), data });
  }

  updateFlow<T extends AnalyticsEventName>(
    event: T,
    data: Partial<AnalyticsEventMap[T]>
  ): void {
    const flow = this.getFlow(event);
    if (!flow) return;
    this.setFlow(event, { ...flow, data: { ...flow.data, ...data } });
  }

  cancelFlow(event: AnalyticsEventName): void {
    localStorage.removeItem(this.flowKey(event));
  }

  hasFlow(event: AnalyticsEventName): boolean {
    return this.getFlow(event) !== null;
  }

  /**
   * Conclude a flow: emits `event` with accumulated data + duration_ms.
   * No-op when never started. Deferred 50ms so the conclusion orders
   * strictly after its trigger step in ClickHouse; pagehide flushes pending
   * sends so gateway redirects don't drop completions.
   */
  endFlow<T extends AnalyticsEventName>(
    event: T,
    properties: Partial<AnalyticsEventMap[T]> = {}
  ): void {
    if (!this.op) return;
    const flow = this.getFlow(event);
    this.cancelFlow(event);
    if (!flow) return;
    const durationMs = Date.now() - flow.startedAt;
    const payload = {
      ...flow.data,
      ...(properties as Record<string, unknown>),
      // Stale flows surviving in localStorage for days are noise — cap 6h.
      ...(durationMs <= 21_600_000 ? { duration_ms: durationMs } : {}),
    };
    const timer = window.setTimeout(() => {
      this.pendingConclusions.delete(timer);
      this.op?.track(event, payload);
    }, 50);
    this.pendingConclusions.set(timer, () => this.op?.track(event, payload));
  }

  private flushPendingConclusions(): void {
    this.pendingConclusions.forEach((send, timer) => {
      window.clearTimeout(timer);
      send();
    });
    this.pendingConclusions.clear();
  }

  private flowKey = (event: string): string =>
    `${this.flowPrefixKey} - ${event}`;

  private getFlow(event: string): FlowState | null {
    try {
      const raw = localStorage.getItem(this.flowKey(event));
      return raw ? (JSON.parse(raw) as FlowState) : null;
    } catch {
      return null;
    }
  }

  private setFlow(event: string, state: FlowState): void {
    try {
      localStorage.setItem(this.flowKey(event), JSON.stringify(state));
    } catch {
      // Storage full/unavailable — the concluding event just loses duration.
    }
  }
}

export const analytics = new AnalyticsService();
